import { create } from "zustand";

/**
 * Auth store — minimal, IN-MEMORY, NOT persisted.
 *
 * Holds ONLY the short-lived access token and its derived status. On reload the token is
 * gone and is re-obtained via the httpOnly refresh cookie (the axios refresh flow). The user
 * profile / bundles / surfaces are SERVER STATE and live in TanStack Query (a future
 * useMe-style query) — never here. No persist middleware: nothing on disk, by design.
 */
interface AuthState {
  /** Short-lived access token; null = logged out. In memory only. */
  accessToken: string | null;
  /** Derived from `accessToken !== null`; kept in sync by the actions below. */
  isAuthenticated: boolean;
  /** Store a new access token (e.g. after login or a successful refresh). */
  setAccessToken: (token: string | null) => void;
  /** Clear the token → logged out. */
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  isAuthenticated: false,
  setAccessToken: (token) =>
    set({ accessToken: token, isAuthenticated: token !== null }),
  clearAuth: () => set({ accessToken: null, isAuthenticated: false }),
}));

/**
 * Read the access token OUTSIDE React (the axios interceptor is not a component). Uses
 * `getState()`, so it reflects the latest token without a subscription.
 */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}
